<template>
  <div v-if="hasRating">
    <div v-if="compactRating" class="inline-flex gap-2 items-center text-sm">
      <span
        class="bg-petrol-500 text-white rounded-md px-2 py-1.5 font-semibold inline-flex gap-1 items-center"
      >
        <UiIcon
          src="/svg/icons/star.svg"
          width="14"
          height="14"
          aria-hidden="true"
          class="fill-white inline-block"
        />
        <span aria-hidden="true">
          {{ formatNumber(props.rating) }}
        </span>
      </span>
      <span v-if="props.numberOfReviews > 0">
        {{ t(`number_of_reviews.${numerus(props.numberOfReviews)}`, { reviews: props.numberOfReviews }) }}
      </span>
    </div>
    <div v-if="compactRatingCard" class="inline-flex gap-0.5 items-center text-sm">
      <span
        class="rounded-md font-semibold inline-flex gap-1 items-center"
      >
        <UiIcon
          src="/svg/icons/star.svg"
          width="14"
          height="14"
          aria-hidden="true"
          class="fill-orange-500 inline-block"
        />
        <span aria-hidden="true">
          {{ formatNumber(props.rating) }}
        </span>
      </span>
      <span v-if="props.numberOfReviews > 0" class="text-neutral-600">
        ({{ t(`number_of_reviews.${numerus(props.numberOfReviews)}`, { reviews: props.numberOfReviews }) }})
      </span>
    </div>
    <div v-else :class="[{'flex w-auto text-sm text-neutral-800 font-semibold items-center' : ratingText}]">
      <div v-show="ratingText && props.rating >= 3.5" class="mr-1.5">
        <UiLanguagePlaceholder v-if="props.rating >= 3.5 && props.rating < 4" domain="SharedRating" name="rating_good" />
        <UiLanguagePlaceholder v-if="props.rating >= 4.0 && props.rating < 4.5" domain="SharedRating" name="rating_very_good" />
        <UiLanguagePlaceholder v-if="props.rating >= 4.5 && props.rating < 4.8" domain="SharedRating" name="rating_great" />
        <UiLanguagePlaceholder v-if="props.rating >= 4.8 && props.rating <= 5.0" domain="SharedRating" name="rating_outstanding" />
      </div>
      <template v-if="svg">
        <UiIcon
          v-for="(star, index) in stars"
          :key="index"
          :src="`/svg/icons/star-${star}.svg`"
          :width="props.width"
          :height="props.height"
          class="inline-block"
          :class="[$attrs.class]"
          aria-hidden="true"
          :alt="`Star` + index"
        />
      </template>
      <template v-if="!svg">
        <img
          v-for="(star, index) in stars"
          :key="index"
          :src="`/svg/icons/star-${star}.svg`"
          :width="props.width"
          :height="props.height"
          class="inline-block"
          :class="[$attrs.class]"
          aria-hidden="true"
          :alt="`Star` + index"
        >
      </template>
    </div>
  </div>
</template>

<script setup>
import { formatNumber } from '~~/utils/Utils'
const { translate: t, numerus } = useTranslations('SharedRating')
const compactRating = computed(() => props.design === 'compact')
const compactRatingCard = computed(() => props.design === 'compact_card')
function getStar (value, halfFrom, fullFrom) {
  if (value >= fullFrom) {
    return 'full'
  } else if (value >= halfFrom) {
    return 'half'
  } else {
    return 'empty'
  }
}

const props = defineProps({
  rating: {
    type: Number,
    default: 5
  },
  numberOfReviews: {
    type: Number,
    default: 0
  },
  width: {
    type: [String, Number],
    default: '20'
  },
  height: {
    type: [String, Number],
    default: '20'
  },
  ratingText: {
    type: Boolean,
    default: false
  },
  svg: {
    type: Boolean,
    default: true
  },
  design: {
    type: String,
    default: 'standard'
  }
})

defineOptions({
  inheritAttrs: false
})

const stars = computed(function () {
  return [
    getStar(parseFloat(props.rating), 0.3, 0.8),
    getStar(parseFloat(props.rating), 1.3, 1.8),
    getStar(parseFloat(props.rating), 2.3, 2.8),
    getStar(parseFloat(props.rating), 3.3, 3.8),
    getStar(parseFloat(props.rating), 4.3, 4.8)
  ]
})

const hasRating = computed(() => parseFloat(props.rating) > 0)
</script>

